import * as bootstrap from 'bootstrap';
import axios from 'smg/axios';
import * as models from 'smg/models';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';

interface myWindow extends Window {
    readonly cloudFront: string;
}

declare const window: myWindow;

declare interface uploadResp extends models.Upload {
    hasRekognition?: boolean;
    labels: {
        label: string;
        confidence: number;
    }[];
    moderationLabels: {
        label: string;
        confidence: number;
    }[];
    combinedScore?: number;
}

declare interface uploadsResp {
    uploads: uploadResp[];
}

export class Uploader extends uploads.Uploader {
    private $label: HTMLLabelElement;

    constructor($label: HTMLLabelElement, opts: uploads.UploadOptions = {}) {
        const $container = $label.closest('.uploader-container') as HTMLElement;
        const $thumbTemplate = $container.querySelector('.uploader-thumbnail-template') as HTMLTemplateElement;
        const $thumbContainer = $container.querySelector('.uploader-thumbnails') as HTMLElement;

        const $deleteConfirmTemplate = $container.querySelector('.uploader-thumbnail-delete-confirm-template') as HTMLTemplateElement;

        opts.thumbnails = false;
        opts.handlers = () => {
            const $thumb = template.Clone($thumbTemplate).firstElementChild as HTMLElement;
            const $a = $thumb.querySelector('.uploader-thumbnail-img-link') as HTMLAnchorElement;
            const $img = $thumb.querySelector('.uploader-thumbnail-img') as HTMLImageElement;
            const $name = $thumb.querySelector('.uploader-thumbnail-name') as HTMLElement;

            const $delete = $thumb.querySelector('.uploader-thumbnail-delete') as HTMLButtonElement;
            $delete.addEventListener('click', () => {
                const $modal = $delete.closest<HTMLElement>('.modal');

                const t = new bootstrap.Tooltip($thumb, {
                    trigger: 'manual',
                    placement: 'top',
                    title: 'Are you sure you want to delete this image?',
                    html: true,
                    offset: [0, 16],
                    container: $modal ?? false,
                });

                const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

                const $confirm = $deleteConfirm.querySelector('.uploader-thumbnail-delete-confirm-button') as HTMLButtonElement;
                $confirm.addEventListener('click', () => {
                    $thumb.remove();
                    t.dispose();
                    this.Remove(uploadID);
                });

                const $cancel = $deleteConfirm.querySelector('.uploader-thumbnail-delete-cancel-button') as HTMLButtonElement;
                $cancel.addEventListener('click', () => t.dispose());

                t.setContent({ '.tooltip-inner': $deleteConfirm });
                t.show();
            });

            let uploadID: number;

            const handlerFuncs: uploads.HandlerFuncs = {
                onSelected: (blobURL, name) => {
                    $a.href = blobURL;
                    $a.setAttribute('data-caption', name);

                    void uploads.Load($img, blobURL, name).then(success => {
                        if (!success) {
                            $thumb.classList.add('uploader-thumbnail--no-preview');
                        }
                    });

                    $name.innerText = name;

                    $thumbContainer.appendChild($thumb);
                },
                onUploadCompleted: id => {
                    uploadID = id;
                    $thumb.classList.remove('uploader-thumbnail--loading');
                },
            };
            return handlerFuncs;
        };

        super($label, opts);

        if (opts.uploadIDs?.length) {
            void axios.get<uploadsResp>('/uploads', {
                params: {
                    'q': `id:${opts.uploadIDs.join(',')}`,
                },
            }).then(resp => {
                resp.data.uploads.forEach(u => {
                    if (!u.id) {
                        return;
                    }
                    const $thumb = template.Clone($thumbTemplate).firstElementChild as HTMLElement;
                    const $a = $thumb.querySelector('.uploader-thumbnail-img-link') as HTMLAnchorElement;
                    const $img = $thumb.querySelector('.uploader-thumbnail-img') as HTMLImageElement;
                    const $name = $thumb.querySelector('.uploader-thumbnail-name') as HTMLElement;

                    const $delete = $thumb.querySelector('.uploader-thumbnail-delete') as HTMLButtonElement;
                    $delete.addEventListener('click', () => {
                        const t = new bootstrap.Tooltip($thumb, {
                            trigger: 'manual',
                            placement: 'top',
                            title: 'Are you sure you want to delete this image?',
                            html: true,
                            offset: [0, 16],
                        });

                        const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

                        const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
                        $confirm.addEventListener('click', () => {
                            $thumb.remove();
                            t.dispose();
                            if (u.id) {
                                this.Remove(u.id);
                            }
                        });

                        const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
                        $cancel.addEventListener('click', () => t.dispose());

                        t.setContent({ '.tooltip-inner': $deleteConfirm });
                        t.show();
                    });

                    const src = `${window.cloudFront ?? ''}/${u.key ?? ''}`;
                    $a.href = src;
                    $a.setAttribute('data-caption', u.name ?? '');
                    $name.innerText = u.name ?? '';

                    void uploads.Load($img, src, u.name ?? '').then(success => {
                        if (!success) {
                            $thumb.classList.add('uploader-thumbnail--no-preview');
                        }
                        $thumb.classList.remove('uploader-thumbnail--loading');
                    });

                    $thumbContainer.appendChild($thumb);
                });
            });
        }

        this.$label = $label;
    }

    Clear() {
        super.Clear();
        const $container = this.$label.closest('.uploader-container') as HTMLElement;
        const $thumbContainer = $container.querySelector('.uploader-thumbnails') as HTMLElement;
        $thumbContainer.innerHTML = '';
    }
}
